import { useEffect, useState } from "react";
import {
  RecommendationsClient,
  RecommendationsDto,
} from "../../utilities/backend/client";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogTitle,
  IconButton,
  Paper,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import CloseIcon from "@mui/icons-material/Close";
import { useTheme } from "@mui/material/styles";
import { ExportButton, StyledTableCell } from "../../styles/StyledComponents";

const RecommendationsAdmin = () => {
  const recommendationsClient = new RecommendationsClient();
  const [recommendations, setRecommendations] =
    useState<RecommendationsDto[]>();
  const [filterRecommendations, setFilterRecommendations] =
    useState<RecommendationsDto[]>();

  const [selectedRecommendation, setSelectedRecommendation] =
    useState<RecommendationsDto | null>(null);

  const [notificationShow, setNotificationShow] = useState<boolean>(false);
  const [notificationText, setNotificationText] = useState<string>();
  const [dialogText, setDialogText] = useState<string>();
  const [isReject, setIsReject] = useState<boolean>();
  const [loading, setLoading] = useState<boolean>(true);
  const [isExporting, setIsExporting] = useState<boolean>(false);

  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");
  const [sortField, setSortField] = useState<"investmentName" | "status">(
    "status"
  );

  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("md"));

  const handleClickOpenDialog = (
    recommendation: RecommendationsDto,
    text: string,
    isReject: boolean
  ) => {
    setSelectedRecommendation(recommendation);
    setDialogText(text);
    setIsReject(isReject);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleExportAll = () => {
    setIsExporting(true);
    recommendationsClient
      .exportRecommendations()
      .then(() => {
        console.log("Export successful");
      })
      .catch((error) => {
        console.error("Error exporting users", error);
      })
      .finally(() => {
        setIsExporting(false);
      });
  };

  useEffect(() => {
    setLoading(true);
    recommendationsClient
      .recommendations_GetAllRecommendations()
      .then((response) => {
        setRecommendations(response);
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        setLoading(false);
      });
  }, [notificationText]);

  const handleNotificationClose = (
    event?: React.SyntheticEvent | Event,
    reason?: string
  ) => {
    if (reason === "clickaway") {
      return;
    }

    setNotificationShow(false);
  };

  const onAcceptClick = (recommendation: RecommendationsDto) => {
    recommendation.status = "approved";
    recommendationsClient
      .recommendations_UpdateRecommendation(recommendation.id!, recommendation)
      .then(async () => {
        setNotificationText("Recommendation approved");
        setNotificationShow(true);
      })
      .catch((error) => {
        console.log(error);
      });
    handleClose();
  };

  const onDeclineClick = (recommendation: RecommendationsDto) => {
    recommendation.status = "reject";
    recommendationsClient
      .recommendations_UpdateRecommendation(recommendation.id!, recommendation)
      .then(async () => {
        setNotificationText("Recommendation rejected");
        setNotificationShow(true);
      })
      .catch((error) => {
        console.log(error);
      });
    handleClose();
  };

  const toggleSortDirection = (field: "investmentName" | "status") => {
    setSortField((prevField) => {
      if (prevField === field) {
        setSortDirection((prevDirection) =>
          prevDirection === "asc" ? "desc" : "asc"
        );
      } else {
        setSortField(field);
        setSortDirection("asc");
      }
      return field;
    });
  };

  useEffect(() => {
    if (recommendations && recommendations.length > 0) {
      const sortedRecommendations = [...recommendations].sort((a, b) => {
        if (sortField === "status") {
          const statusOrder: { [key: string]: number } = {
            pending: 1,
            approved: 2,
            reject: 3,
          };

          const statusA = statusOrder[a.status || ""] || 0;
          const statusB = statusOrder[b.status || ""] || 0;

          if (statusA !== statusB) {
            return sortDirection === "asc"
              ? statusA - statusB
              : statusB - statusA;
          }

          return (b.id ?? 0) - (a.id ?? 0);
        }

        if (sortField === "investmentName") {
          const investmentNameA = a.campaignName
            ? a.campaignName.toLowerCase()
            : "";
          const investmentNameB = b.campaignName
            ? b.campaignName.toLowerCase()
            : "";

          const investmentNameComparison =
            investmentNameA.localeCompare(investmentNameB);

          return sortDirection === "asc"
            ? investmentNameComparison
            : -investmentNameComparison;
        }

        return 0;
      });

      setFilterRecommendations(sortedRecommendations);
    }
  }, [sortDirection, sortField, recommendations]);

  return (
    <>
      {loading ? (
        <Box sx={{ margin: "0 auto" }}>
          <CircularProgress />
        </Box>
      ) : (
        <div
          style={{
            display: "grid",
            gap: "10px",
          }}
        >
          <ExportButton
            variant="contained"
            color="primary"
            onClick={handleExportAll}
            disabled={isExporting}
          >
            Export All
          </ExportButton>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledTableCell>User Full Name</StyledTableCell>
                  <StyledTableCell>ID</StyledTableCell>
                  <StyledTableCell>User Email</StyledTableCell>
                  <StyledTableCell
                    onClick={() => toggleSortDirection("investmentName")}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    {sortDirection === "asc" ? (
                      <span>&uarr;</span>
                    ) : (
                      <span>&darr;</span>
                    )}{" "}
                    Investment Name
                  </StyledTableCell>
                  <StyledTableCell>Amount</StyledTableCell>
                  <StyledTableCell>Date Created</StyledTableCell>
                  <StyledTableCell
                    onClick={() => toggleSortDirection("status")}
                    sx={{
                      cursor: "pointer",
                    }}
                  >
                    {sortDirection === "asc" ? (
                      <span>&uarr;</span>
                    ) : (
                      <span>&darr;</span>
                    )}{" "}
                    Status
                  </StyledTableCell>
                  <StyledTableCell>Action</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filterRecommendations ? (
                  filterRecommendations.map((recommendation) => (
                    <TableRow key={recommendation.id}>
                      <TableCell>{recommendation.userFullName}</TableCell>
                      <TableCell>{recommendation.id}</TableCell>
                      <TableCell
                        style={{
                          maxWidth: "200px",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {recommendation.userEmail}
                      </TableCell>
                      <TableCell>{recommendation.campaignName}</TableCell>
                      <TableCell>{recommendation.amount}</TableCell>
                      <TableCell>
                        {recommendation.dateCreated
                          ? new Date(
                              recommendation.dateCreated
                            ).toLocaleDateString("en-US")
                          : ""}
                      </TableCell>
                      <TableCell>{recommendation.status}</TableCell>
                      <TableCell>
                        {recommendation.status === "pending" ? (
                          <div
                            style={{
                              display: "flex",
                              justifyContent: "space-between",
                            }}
                          >
                            <IconButton
                              color="success"
                              onClick={() =>
                                handleClickOpenDialog(
                                  recommendation,
                                  "Do you want to accept recommendation?",
                                  false
                                )
                              }
                            >
                              <CheckIcon />
                            </IconButton>
                            <IconButton
                              color="error"
                              onClick={() =>
                                handleClickOpenDialog(
                                  recommendation,
                                  "Do you want to reject recommendation?",
                                  true
                                )
                              }
                            >
                              <CloseIcon />
                            </IconButton>
                          </div>
                        ) : (
                          <></>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <Dialog
            fullScreen={fullScreen}
            open={open}
            onClose={handleClose}
            aria-labelledby="responsive-dialog-title"
          >
            <DialogTitle id="responsive-dialog-title">{dialogText}</DialogTitle>
            <DialogActions>
              <Button autoFocus onClick={handleClose}>
                No
              </Button>
              <Button
                onClick={() => {
                  isReject
                    ? onDeclineClick(selectedRecommendation!)
                    : onAcceptClick(selectedRecommendation!);
                }}
                autoFocus
              >
                Yes
              </Button>
            </DialogActions>
          </Dialog>
        </div>
      )}

      <Snackbar
        open={notificationShow}
        autoHideDuration={6000}
        onClose={handleNotificationClose}
      >
        <Alert
          onClose={handleNotificationClose}
          severity="success"
          sx={{ width: "100%" }}
        >
          {notificationText}
        </Alert>
      </Snackbar>
    </>
  );
};

export default RecommendationsAdmin;
